/**
 * AppHeader — single source of truth for the app header / brand block.
 *
 * Used by:
 *   • components/home.jsx                        (Datasets landing page)
 *   • process-miner/navigation/AppNav.jsx        (Process Miner nav bar)
 *
 * Props:
 *   iconSrc        {string}    – brand icon image URL
 *   title          {string}    – main heading
 *   subtitle       {string}    – secondary line beneath title
 *   actions        {ReactNode} – right-side buttons (Help, Save, Exit…)
 *   tabs           {Array<{ id, label, icon? }>}
 *   activeTab      {string}    – id of the active tab
 *   onTabClick     {Function}  – (tabId) => void
 *   renderTabIcon  {Function}  – optional (tabId) => ReactNode for custom icons
 *   extraRight     {ReactNode} – rendered to the right of the tab strip
 */

import * as React from 'react';
import styled from 'styled-components';

export const headerTokens = {
  shellBg:        '#FFFFFF',
  shellBorder:    '#DFE1E6',
  title:          '#172B4D',
  muted:          '#6B778C',
  primary:        '#0C66E4',
  primaryTint:    '#E9F2FF',
  primaryBorder:  '#B3D4FF',
  tabText:        '#253858',
  tabHover:       '#F7F8F9',
  tabHoverBorder: '#C1C7D0',
};

const T = headerTokens;

const Shell = styled.div`
  box-sizing: border-box !important;
  padding: 16px 20px 0 20px;
  border-bottom: 1px solid ${T.shellBorder};
  background: ${T.shellBg};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif !important;
  user-select: none;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const BrandIcon = styled.img`
  width: 44px !important;
  height: 44px !important;
  object-fit: contain;
  flex-shrink: 0;
`;

const BrandText = styled.div`
  min-width: 0;
`;

const BrandTitle = styled.div`
  font-size: 18px !important;
  font-weight: 700 !important;
  color: ${T.title};
  line-height: 1.15;
  letter-spacing: -0.01em;
`;

const BrandSubtitle = styled.div`
  margin-top: 2px;
  font-size: 12px !important;
  color: ${T.muted};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const TabRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding-bottom: 8px;
  flex-wrap: wrap;
`;

const TabList = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  appearance: none !important;
  -webkit-appearance: none !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  padding: 8px 14px !important;
  min-height: 40px !important;
  border-radius: 6px !important;
  border: 1px solid ${({ $active }) => ($active ? T.primaryBorder : T.shellBorder)} !important;
  background: ${({ $active }) => ($active ? T.primaryTint : T.shellBg)} !important;
  color: ${({ $active }) => ($active ? T.primary : T.tabText)} !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif !important;
  font-size: 14px !important;
  line-height: 1.25 !important;
  font-weight: ${({ $active }) => ($active ? 700 : 600)} !important;
  cursor: pointer !important;
  transition: all 0.12s ease !important;
  box-shadow: none !important;
  text-decoration: none !important;
  white-space: nowrap !important;

  &:hover {
    background: ${({ $active }) => ($active ? T.primaryTint : T.tabHover)} !important;
    border-color: ${({ $active }) => ($active ? T.primaryBorder : T.tabHoverBorder)} !important;
  }

  &:focus {
    outline: 2px solid #4C9AFF !important;
    outline-offset: 1px !important;
  }
`;

const AppHeader = ({
  iconSrc,
  title,
  subtitle,
  actions,
  tabs = [],
  activeTab,
  onTabClick,
  renderTabIcon,
  extraRight,
}) => (
  <Shell>
    <TopRow>
      <Brand>
        {iconSrc && <BrandIcon src={iconSrc} alt="" />}
        <BrandText>
          <BrandTitle>{title}</BrandTitle>
          {subtitle && <BrandSubtitle>{subtitle}</BrandSubtitle>}
        </BrandText>
      </Brand>
      {actions && <Actions>{actions}</Actions>}
    </TopRow>

    {tabs.length > 0 && (
      <TabRow>
        <TabList>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <Tab key={tab.id} $active={isActive} onClick={() => onTabClick(tab.id)}>
                {Icon && <Icon size={17} />}
                {!Icon && renderTabIcon && renderTabIcon(tab.id)}
                {tab.label}
              </Tab>
            );
          })}
        </TabList>
        {extraRight && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {extraRight}
          </div>
        )}
      </TabRow>
    )}
  </Shell>
);

export default AppHeader;
